import { isSameMonth } from 'date-fns';
import { getMatrixValue, setMatrixValueBasedOnTheSize } from './sizeMatrix';
import { getTotalMonths, getTotalRemainingDays, getTotalDaysOfCurrentStayedInMonth, isDateBefore30Days } from './timing';

export function round(value) {
  return Math.round(value * 100) / 100;
}

/**
 * Converts a date to a facility year month integer.
 * Example:
 *  Oct 1 at 12pm → 24310
 *  Oct 3 at 5pm  → 24310  (same month!)
 *  24310 <= 24310 → true ✓
 * @param {Date} d - The date to convert.
 * @param {number} offsetMinutes - The offset in minutes.
 * @returns {number} - The facility year month.
 */
function toFacilityYearMonth(d, offsetMinutes) {
  const ms = (d instanceof Date) ? d.getTime() : new Date(d).getTime();
  const facilityDate = new Date(ms + offsetMinutes * 60 * 1000);
  return facilityDate.getUTCFullYear() * 12 + facilityDate.getUTCMonth();
}

export default function pricing({ unit, qualityPricing, dTier, end, start, prorated }) {
  if (!unit.tierQuality) {
    return 'Tier Quality not selected.';
  }

  if (!unit.size) {
    return 'Size Range not selected.';
  }

  const sizeValueOfUnit = getMatrixValue(unit.size, unit.type);
  if (!qualityPricing && qualityPricing[unit.tierQuality] !== undefined) {
    return 'Facility Price not set';
  }

  const tierQualityValue = qualityPricing[unit.tierQuality] || 1;

  const [sqPrice] = !dTier
    ? []
    : dTier.filter((tier) => {
      if (tier.range === sizeValueOfUnit) {
        return tier;
      }
    });

  const price = !sqPrice
    ? tierQualityValue
    : round(tierQualityValue * sqPrice.rate);

  const totalMonths = getTotalMonths(start, end);
  const totalProrateDays = getTotalRemainingDays(start) + 1;
  const totalDaysOfMonth = getTotalDaysOfCurrentStayedInMonth(start);
  if (prorated) {
    const oneDayPrice = unit.type === 'Parking' ? (price) / totalDaysOfMonth :
      (price * unit.computedSize) / totalDaysOfMonth;
    return totalProrateDays > 0 ?
      round(oneDayPrice * totalProrateDays) : 0;
  }
  return round(unit.type === 'Parking' ? price : price * unit.computedSize * totalMonths);
}

export function getLeaseTaxLineItem(newLineItem, facility) {
  let taxValue = 0;
  let description = 'Sales Tax applied';
  let taxRate = facility.rentTaxFlag && facility.tax && facility.tax.rentTaxRate ? facility.tax.rentTaxRate : 0;
  if (facility && facility.tax && facility.tax.rentTaxLabel) {
    description = `${facility.tax.rentTaxLabel} @ ${taxRate}% for Sales`;
  }

  // compute tax based on chargeType
  if (newLineItem.chargeType === 'FEES' && newLineItem.feeType) {
    taxRate = 0; // reset the sales tax rate when fee item.
    description = '';
    let lineItemFeeType = newLineItem.feeType;
    const facilityFeeType = lineItemFeeType.replaceAll(' ', '');
    const feeTaxPricing = facility.feeTypesTaxPricing
      .find(feeType => feeType.type === facilityFeeType);

    if (lineItemFeeType === 'Security' && facility && facility.securityFee && facility.securityFee.label !== '') {
      lineItemFeeType = facility.securityFee.label;
    }
    if (facility.taxOnFeeTypeEnabled && feeTaxPricing) {
      taxRate = feeTaxPricing.rate;
      description = `${newLineItem.feeType} Tax applied`;
      if (facility && facility.tax && facility.tax.rentTaxLabel) {
        description = `${facility.tax.rentTaxLabel} @ ${taxRate}% for ${lineItemFeeType}`;
      }
    }
  } else if (!facility.rentTaxFlag || newLineItem.chargeType === 'DEPOSIT') {
    taxValue = 0;
    taxRate = 0;
  }

  taxValue = newLineItem.amount * (taxRate / 100);
  const taxItem = {
    chargeType: 'TAX',
    details: 'TAX',
    createdAt: new Date(),
    period: newLineItem.period,
    description,
    amount: taxValue,
    accountBalance: newLineItem.accountBalance + taxValue,
    dueDate: newLineItem.dueDate,
    unit: newLineItem.unit,
    type: 'CREDIT',
    sourceType: newLineItem.chargeType,
    ...(newLineItem.feeType ? { sourceFeeType: newLineItem.feeType } : {}),
    custom: !!newLineItem.custom,
    ...(newLineItem.addedBy ? { addedBy: newLineItem.addedBy } : {}),
  };

  return taxItem;
}

export function getPricingBasedOnType(unit, facility) {
  switch (unit.type) {
    case 'Warehouse':
      return facility.pricing;
    case 'Office':
      return facility.officePricing;
    default:
      return facility.parkingPricing;
  }
}

export function computePricingBasedOnType(unit, facility, sizeValueOfUnit) {
  switch (unit.type) {
    case 'Warehouse': {
      const { qualityPricing, dTier } = getPricingBasedOnType(unit, facility);
      const tierQualityValue = qualityPricing[unit.tierQuality] !== 'undefined' ? qualityPricing[unit.tierQuality] : 1;
      const [sqPrice] = !dTier ? [] : dTier.filter(tier => (tier.range === sizeValueOfUnit));
      const price = !sqPrice ? tierQualityValue : round(tierQualityValue * sqPrice.rate);
      return round(price * unit.computedSize);
    }
    case 'Office': {
      const { qualityPricing, dTier } = getPricingBasedOnType(unit, facility);
      const tierQualityValue = qualityPricing[unit.tierQuality] !== 'undefined' ? qualityPricing[unit.tierQuality] : 1;
      const [sqPrice] = !dTier ? [] : dTier.filter(tier => (tier.range === sizeValueOfUnit));
      const price = !sqPrice ? tierQualityValue : round(tierQualityValue * sqPrice.rate);
      return round(price * unit.computedSize);
    }
    default: {
      const { qualityPricing } = getPricingBasedOnType(unit, facility);
      const price = qualityPricing[unit.tierQuality] !== 'undefined' ? qualityPricing[unit.tierQuality] : 1;

      return round(price);
    }
  }
}

/**
* Computes the pricing for a given unit based on its status and other factors.
*
* @param {Object} unit - The unit object containing information about the unit.
* @param {Object} facility - The facility object containing pricing information.
* @param {boolean} [stillFull=true] - Indicates whether to calculate pricing with still full price applied.
* @returns {number} The computed unit pricing.
*/
export function computeUnitPricing(unit, facility, stillFull = true) {
  const size = setMatrixValueBasedOnTheSize(unit.computedSize, unit.type);
  const sizeValueOfUnit = getMatrixValue(size, unit.type);

  switch (unit.status) {
    case 'AVAILABLE': {
      return computePricingBasedOnType(unit, facility, sizeValueOfUnit);
    }
    // Cascading case satements, logic is same for all the below status types
    case 'MOVING_OUT':
    case 'OVERDUE_MOVEOUT':
    case 'RENTED':
    case 'MOVEOUT_RESERVED': {
      const unitPrice = computePricingBasedOnType(unit, facility, sizeValueOfUnit);
      if (stillFull && facility.stillFullFlag) {
        const stillFullPrice = unitPrice * (facility.stillFullPrice / 100);
        return unitPrice + stillFullPrice;
      }
      return unitPrice;
    }
    case 'UNAVAILABLE': {
      return 0;
    }
    default: {
      const unitRental = unit.activeUnitRental ? { ...unit.activeUnitRental } : { ...facility };
      if (unit.activeUnitRental && !unit.activeUnitRental.pricing) {
        unitRental.pricing = facility.pricing;
      }

      if (unit.activeUnitRental && !unit.activeUnitRental.officePricing) {
        unitRental.officePricing = facility.officePricing;
      }

      if (unit.activeUnitRental && !unit.activeUnitRental.parkingPricing) {
        unitRental.parkingPricing = facility.parkingPricing;
      }

      return computePricingBasedOnType(unit, unitRental, sizeValueOfUnit);
    }
  }
}

export function getLienDueAmount(creditLineItemsByPeriod = [], activeLease, facilityOffset, date = new Date()) {
  if (!creditLineItemsByPeriod || !creditLineItemsByPeriod.length) { return 0; }

  const docYearMonth = toFacilityYearMonth(date, facilityOffset);

  const leaseTotalAmountDue = creditLineItemsByPeriod
    .filter((item) => toFacilityYearMonth(item.period, facilityOffset) <= docYearMonth)
    .map(item => item.total)
    .reduce((a, b) => a + b, 0);

  const dueAmount = leaseTotalAmountDue - activeLease.amountPaid;
  if (dueAmount < 0) { return 0; }

  return dueAmount;
}

export function getPreLienDueAmount(
  creditLineItemsByPeriod = [],
  customerDocs = [],
  activeLease,
  facilityOffset,
  options = {},
) {
  if (!creditLineItemsByPeriod || !creditLineItemsByPeriod.length) { return 0; }

  const { skipPreviousPreLienAmount = false, currentDate = new Date() } = options;
  const activeLeaseId = activeLease['_id']; // eslint-disable-line dot-notation
  const preLienDocs = customerDocs.filter(item =>
    item.type === 'PRELIMINARY_LIEN_NOTICE' && item.referenceId === activeLeaseId);
  preLienDocs.sort((item1, item2) =>
    new Date(item2.createdAt) - new Date(item1.createdAt));
  const [foundDoc] = preLienDocs;
  let foundDocDate = null;

  // only add foundDocDate if foundDoc belongs to current month.
  if (foundDoc) {
    const docDate = new Date(foundDoc.createdAt);
    if (isDateBefore30Days(docDate, currentDate)) {
      foundDocDate = foundDoc.createdAt;
    } else {
      foundDocDate = null;
    }
  }

  if (
    !skipPreviousPreLienAmount &&
    foundDoc &&
    foundDoc.preLienAmount &&
    foundDocDate
  ) {
    return foundDoc.preLienAmount;
  }

  const date = foundDocDate
    ? new Date(foundDoc.createdAt)
    : new Date(currentDate.getFullYear(), currentDate.getMonth(), 2);
  const docYearMonth = toFacilityYearMonth(date, facilityOffset);

  const leaseTotalAmountDue = creditLineItemsByPeriod
    .filter(item => toFacilityYearMonth(item.period, facilityOffset) <= docYearMonth)
    .map(item => item.total)
    .reduce((a, b) => a + b, 0);

  const dueAmount = leaseTotalAmountDue - activeLease.amountPaid;
  if (dueAmount < 0) { return 0; }

  return dueAmount;
}



// WEBPACK FOOTER //
// ./src/utils/pricing.js